/**
 * OCR追加プロンプト設定コンポーネント
 *
 * AIの読み取り精度を向上させるための追加指示を設定するUI
 */

import { Text, Box, Paper, Stack, Group, Badge, Accordion, Textarea } from "@mantine/core";
import { HelpCircle } from "lucide-react";
import { OcrConfig } from "../../services/csvSettingsService";

interface OcrPromptSettingsProps {
  ocrConfig: OcrConfig;
  onChangeOcrConfig: (config: OcrConfig) => void;
}

/**
 * OCR追加プロンプト設定
 */
export function OcrPromptSettings({ ocrConfig, onChangeOcrConfig }: OcrPromptSettingsProps) {
  return (
    <Paper withBorder p="md" bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))">
      <Stack gap="xs">
        <Group justify="space-between">
          <Group gap="xs">
            <Text component="span" size="sm" fw={500}>追加プロンプト (OCR設定)</Text>
            <Badge size="xs" variant="light" color="gray">任意</Badge>
          </Group>
        </Group>
        <Text size="xs" c="dimmed">
          基本的には入力不要です。読み取り結果が思わしくない場合や、特定の出力形式（例：「日付はYYYY/MM/DD」など）を指定したい場合にのみ利用してください。
        </Text>
        
        <Accordion variant="contained" radius="md" chevronPosition="left" mb="xs">
          <Accordion.Item value="ocr-hints">
            <Accordion.Control icon={<HelpCircle size={16} />} p="xs">
              <Text size="xs">プロンプトの記述例とヒントを表示</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="sm">
                <Box>
                  <Text size="xs" fw={700} mb={4}>本機能の役割</Text>
                  <Box component="ul" style={{ margin: 0, paddingLeft: '1.2rem' }}>
                    <Text component="li" size="xs">
                      <strong>抽出精度の向上:</strong> 「商品コードは必ず10桁」「数字の『0』と英字の『O』が混同されやすいので注意」といった指示を出すことで、誤認識を減らせます。
                    </Text>
                    <Text component="li" size="xs" mt={4}>
                      <strong>データ加工の自動化:</strong> 「金額にマイナス記号があれば赤字として扱う」といった抽出後のロジックをAIに委ねられます。
                    </Text>
                  </Box>
                </Box>
                
                <Box>
                  <Text size="xs" fw={700} mb={4}>書き方のヒント</Text>
                  <Text size="xs">
                    「〜してください」「〜として解釈してください」といった自然な文章で記述してください。具体的であるほどAIは正確に理解します。
                  </Text>
                </Box>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        <Textarea
          placeholder="例: 日付はYYYY/MM/DD形式で出力してください"
          minRows={3}
          autosize
          value={ocrConfig.promptAddition || ""}
          onChange={(e) =>
            onChangeOcrConfig({
              ...ocrConfig,
              promptAddition: e.currentTarget.value,
            })
          }
        />
      </Stack>
    </Paper>
  );
}
